/**
 *  Billing script file
**/
import authService, { USER_ROLES } from '@/api/authService'
import campaignsService from '@/api/campaignsService'
import customFormatters from '@/filters/customFormatters'
import { GETTERS, MODULE_NAME as MODULE_NAME_FILTERS } from '@/store/filters/constants'
import { CHANNEL_OPTIONS, CHANNEL_LABELS, CHANNEL_COLORS } from '@/utils/campaignOptions'
import {
  MODULE_NAME as MODULE_NAME_SITE,
  ACTIONS as ACTIONS_SITE
} from '@/store/site/constants'
import downloadBlob from '@/utils/downloadBlob'
import stringCompare from '@/utils/stringCompare'
import { mapGetters, mapActions } from 'vuex'
import { EXCEL_MIME_TYPE } from '@/utils/csvUtils'

const REGENERATING_POLLING_INTERVAL = 5000 // ms

export default {
  name: 'Billing',
  mixins: [customFormatters],
  components: {
    InputSmall: () => import(/* webpackChunkName: "InputSmall" */ '@/components/common/input-small/InputSmall.vue'),
    Checkbox: () => import(/* webpackChunkName: "Checkbox" */ '@/components/common/checkbox/Checkbox.vue')
  },
  props: {},
  data () {
    return {
      authData: authService.authData,
      loading: false,
      billing: [],
      billingRequestOpen: null,
      summaryDownloadLoading: false,
      modalOpen: null,
      modalLoading: false,
      regeneratingInterval: null,
      regenerationState: null,
      isRegenerating: false
    }
  },
  computed: {
    ...mapGetters(MODULE_NAME_FILTERS, {
      filters: GETTERS.GET_ROUTE_FILTERS
    }),
    finishDateFilter () {
      const filter = this.filters.find(f => f.key === 'finish_date')
      return filter && filter.value
    },
    isAdmin () {
      return this.authData.user.role === USER_ROLES.AGENCY || this.isRoot
    },
    isRoot () {
      return this.authData.user.role === USER_ROLES.ROOT
    },
    billingData () {
      return this.billing.map(shop => {
        shop.fee = 0
        shop.clicks = 0
        shop.impressions = 0
        shop.campaign_count = 0
        shop.channels = {}
        shop.metrics = shop.metrics || []
        this.$set(shop, 'loading', false)
        this.$set(shop, 'editingPO', false)
        this.$set(shop, 'selected', false)
        for (const channel in CHANNEL_OPTIONS) {
          shop.channels[channel] = {
            fee: 0,
            clicks: 0,
            impressions: 0,
            campaign_count: 0,
            name: CHANNEL_LABELS[channel],
            style: { 'background-color': CHANNEL_COLORS[channel] },
            campaigns: []
          }
        }
        for (const campaign of shop.metrics) {
          shop.campaign_count += 1
          for (const channel in CHANNEL_OPTIONS) {
            if (!campaign[channel]) {
              continue
            }

            const { fee, clicks, impressions, virtual, user } = campaign[channel]
            shop.fee += Number(fee)
            shop.clicks += Number(clicks)
            shop.impressions += Number(impressions)
            if (user) {
              shop.updated_by = user
            }

            shop.channels[channel].fee += Number(fee)
            shop.channels[channel].clicks += Number(clicks)
            shop.channels[channel].impressions += Number(impressions)
            if (virtual) {
              shop.channels[channel].campaign_count += 1
            }
            if (user) {
              shop.channels[channel].updated_by = user
            }
            const id = campaign.campaign_id
            shop.channels[channel].campaigns.push({
              id,
              name: campaign.campaign_name,
              channel,
              channel_label: CHANNEL_LABELS[channel],
              color: CHANNEL_COLORS[channel],
              fee: Number(fee),
              clicks: Number(clicks),
              impressions: Number(impressions),
              virtual: Boolean(virtual),
              updated_by: user
            })
          }
        }

        return shop
      }).sort((a, b) => {
        if (!a.shop && b.shop) {
          return -1
        }
        if (a.shop && !b.shop) {
          return 1
        }
        return stringCompare(a.shop, b.shop)
      })
    },
    selectedShopNumber () {
      return this.billingData.filter(s => s.selected).length
    },
    someShopSelected () {
      return this.selectedShopNumber > 0
    },
    partialShopSelection () {
      return this.someShopSelected && this.selectedShopNumber !== this.billingData.length
    },
    monthYearParams () {
      let date = new Date(this.finishDateFilter)
      const today = new Date()
      if (date.getTime() > today.getTime()) {
        date = today
      }

      const month = date.getMonth() + 1
      const year = date.getFullYear()
      return { month, year }
    }
  },
  mounted () {
    this.setRegerationLoadingInterval()
    this.setSummaryLoadingInterval()
    this[ACTIONS_SITE.SET_BILLING_NOTIFICATION]({ payload: false })
  },
  watch: {
    finishDateFilter: {
      immediate: true,
      handler (value, oldValue) {
        if (value && value !== oldValue) {
          this.fetchBilling()
        }
      }
    }
  },
  methods: {
    ...mapActions(MODULE_NAME_SITE, [ACTIONS_SITE.SET_BILLING_NOTIFICATION]),
    async fetchBilling (toggleLoading = true) {
      if (toggleLoading) {
        this.loading = true
      }
      try {
        const data = await campaignsService.getBilling(this.monthYearParams, this.filters)
        this.billing = data
      } catch (err) {
        // eslint-disable-next-line
        console.error('[Billing.js] Error fetching billing:', err)
      }
      this.loading = false
    },
    startBillingRequest (shop) {
      this.billingRequestOpen = shop.id
    },
    async confirmBillingRequest (shop) {
      shop.loading = true
      try {
        await campaignsService.closeBilling(shop.id)
        await this.fetchBilling(false)
        // shop.closed = true
        this.billingRequestOpen = null
      } catch (err) {
        // eslint-disable-next-line
        console.error('[Billing.js] Error closing billing:', err)
      }
      shop.loading = false
    },
    async regenerateBilling () {
      this.modalOpen = null
      if (!this.billing || !this.billing.length) return
      this.isRegenerating = true
      try {
        await campaignsService.regenerateBilling({
          id: this.billing[0].id
        })
        this.billing = []
        this.setRegerationLoadingInterval()
      } catch (err) {
        // eslint-disable-next-line
        console.error('[Billing.js] Error regenerating billing:', err)
      }
    },
    async getBillingRegenerationState () {
      try {
        const data = await campaignsService.getBillingRegenerationState()
        this.regenerationState = data.state
        if (this.regenerationState === 'SUCCESS') {
          this.fetchBilling(false)
          this.clearRegenerationLoadingInterval()
        } else {
          this.isRegenerating = true
        }
      } catch (err) {
        // eslint-disable-next-line
        console.error('[admin.js] Error fetching regeneration state', err)
      }
    },
    setRegerationLoadingInterval () {
      this.isRegenerating = true
      this.clearRegenerationLoadingInterval()
      this.regeneratingInterval = setInterval(() => {
        this.getBillingRegenerationState()
      }, REGENERATING_POLLING_INTERVAL)
    },
    clearRegenerationLoadingInterval () {
      if (this.regeneratingInterval) {
        clearInterval(this.regeneratingInterval)
      }
      this.isRegenerating = false
    },
    clearSummaryLoadingInterval () {
      if (this.summaryInterval) {
        clearInterval(this.summaryInterval)
      }
      this.summaryDownloadLoading = false
    },
    setSummaryLoadingInterval () {
      this.summaryDownloadLoading = true
      this.clearSummaryLoadingInterval()
      this.summaryInterval = setInterval(() => {
        this.getBillingSummaryState()
      }, REGENERATING_POLLING_INTERVAL)
    },
    async getBillingSummaryState () {
      try {
        const data = await campaignsService.getBillingSummaryState(this.monthYearParams, this.filters)
        this.regenerationState = data.state
        if (this.regenerationState === 'SUCCESS') {
          this.clearSummaryLoadingInterval()
        } else {
          this.setSummaryLoadingInterval()
          this.summaryDownloadLoading = true
        }
      } catch (err) {
        // eslint-disable-next-line
        console.error('[admin.js] Error fetching regeneration state', err)
      }
    },
    async downloadSummary () {
      this.summaryDownloadLoading = true
      try {
        const data = await campaignsService.getBillingSummaryCSV(this.monthYearParams, this.filters)
        if (data) {
          const { month, year } = this.monthYearParams
          downloadBlob(data, EXCEL_MIME_TYPE, `campaigns_summary_${year}_${month}.xlsx`)
          this.summaryDownloadLoading = false
        } else {
          this.setSummaryLoadingInterval()
          this.summaryDownloadLoading = true
        }
      } catch (err) {
        // eslint-disable-next-line
        console.error('[campaigns.js] Error downloading campaigns summary', err)
        this.summaryDownloadLoading = false
      }
    },
    noop () {},
    togglePOEdit (shop) {
      this.$set(shop, 'editingPO', true)
    },
    async savePO (shop) {
      shop.loading = true
      try {
        await campaignsService.updateBilling(shop)
        shop.editingPO = false
      } catch (err) {
        // eslint-disable-next-line
        console.error('[Billing.js] Error updating billing:', err)
      }
      shop.loading = false
    },
    openConfirmModal () {
      this.modalOpen = { type: 'confirm' }
    },
    toggleEditModal (campaign, shop) {
      this.modalOpen = { type: 'metrics', campaign, shop }
    },
    closeModal () {
      this.modalOpen = null
    },
    async updateMetrics () {
      this.modalLoading = true
      try {
        const { id: campaignID, channel, impressions, clicks, fee } = this.modalOpen.campaign
        const { id: shopID, metrics, po } = this.modalOpen.shop
        const editedMetrics = metrics.map(m => {
          const id = m.campaing_id || m.campaign_id
          if (id === campaignID) {
            m[channel].impressions = Number(impressions)
            m[channel].clicks = Number(clicks)
            m[channel].fee = Number(fee)
            m[channel].updated_at = new Date().toJSON()
            const { first_name: f, last_name: l } = this.authData.user
            m[channel].user = [f, l].join(' ')
          }
          return m
        })

        await campaignsService.updateBilling({ available: false, id: shopID, po, metrics: editedMetrics })
        // await this.fetchBilling()
        this.modalOpen = null
      } catch (err) {
        // eslint-disable-next-line
        console.error('[Billing] Error updating metrics:', err)
      }
      this.modalLoading = false
    },
    async confirmBilling () {
      this.loading = true
      try {
        const promises = []
        const shops = this.billingData.filter(s => s.selected && !s.available)
        for (const shop of shops) {
          promises.push(campaignsService.updateBilling({
            id: shop.id,
            available: true,
            metrics: shop.metrics,
            po: shop.po
          }))
        }
        await Promise.all(promises)
        await this.fetchBilling()
      } catch (err) {
        // eslint-disable-next-line
        console.error('[Billing.js] Error confirming billing', err)
      }
      this.loading = false
    },
    onSelectAllChange (flag) {
      for (const shop of this.billingData) {
        this.$set(shop, 'selected', flag)
      }
    },
    canShowTooltip (item) {
      return this.isAdmin && !!item.updated_by
    }
  }
}
